import { cn } from "@omni/utils/styles";
import { Controller, Path, useFormContext } from "react-hook-form";

import { FormText } from "@/components/Form/Base/formText.component";
import { Input } from "@/components/Form/Base/input.component";
import { Label } from "@/components/Form/Base/label.component";
import { BaseFieldProps } from "@/components/Form/RHF/FormFields/BaseField/BaseField.component";
import {
    FormFieldProps,
    isFormRequired
} from "@/components/Form/RHF/FormFields/formField.types";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { GiftCardRenditions } from "@/components/Product/ProductImage/mappedRenditions";
import { ProductImageContainer } from "@/components/Product/ProductImage/productImage.container";

import { GiftCard, RequiredGiftCardFormValues } from "./giftCardType.schema";

type GiftCardFieldValues = RequiredGiftCardFormValues;

type GiftCardTypesPartialProperties = {
    cards: Array<GiftCard>;
};

type GiftCardTypesPartialProps<T extends GiftCardFieldValues> = FormFieldProps<
    Omit<
        BaseFieldProps<GiftCardFieldValues, "sku">,
        "name" | "required" | "label"
    > &
        GiftCardTypesPartialProperties & {
            required: isFormRequired<T, Extract<Path<T>, "sku">>;
        },
    T
>;

const GiftCardTypesPartial = <T extends GiftCardFieldValues>({
    cards,
    microCopy
}: GiftCardTypesPartialProps<T>) => {
    const { control, setValue } = useFormContext<GiftCardFieldValues>();
    return (
        <>
            <FormText
                inline={true}
                color={"default"}
                className={cn(
                    "tw:-mt-2 tw:mb-2 tw:block",
                    "tw:pt-4 tw:font-bold"
                )}
            >
                {microCopy instanceof Function ? microCopy() : microCopy}
            </FormText>
            <Row>
                {cards.map((card) => {
                    return (
                        <Col
                            key={card.sku}
                            xs={4}
                        >
                            <Label className={cn("tw:block tw:text-center")}>
                                <ProductImageContainer
                                    title={card.title}
                                    medias={card.images[0].medias}
                                    isEcoChequesAccepted={false}
                                    className={cn("tw:cursor-pointer")}
                                    renditions={GiftCardRenditions}
                                />
                                <Controller
                                    name={"sku"}
                                    control={control}
                                    render={({ field }) => {
                                        return (
                                            <Input
                                                {...field}
                                                type={"radio"}
                                                value={card.sku}
                                                checked={
                                                    field.value === card.sku
                                                }
                                                onChange={() => {
                                                    setValue("sku", card.sku);
                                                }}
                                                className={cn(
                                                    "tw:block tw:relative tw:m-auto"
                                                )}
                                            />
                                        );
                                    }}
                                />
                            </Label>
                        </Col>
                    );
                })}
            </Row>
        </>
    );
};

export default GiftCardTypesPartial;
